import dayjs, { Dayjs } from 'dayjs';
import qs from 'qs';
import utc from 'dayjs/plugin/utc';
import config from 'config';
import { Options } from 'ky';
import { useAuthenticatedApiClient } from 'utils/apiClient/base';
import {
  SalesReportsTableRecord,
  BasicReportRecord,
  BatchReportRecord,
  GiftcardReportTableParams,
  GiftcardReportTableResult,
  IncomeTransactionsReportRecord,
  InventoryReportTableParams,
  InventoryReportTableResult,
  MailReportPayload,
  OrdersResponse,
  ProductSoldReportRecord,
  ReportFilterOptions,
  ReturnsResponse,
  SalesReportTableParams,
  TipsSummaryReport,
  TipsSummaryReportParams,
  TipPerTransactionReportTableResult,
  TipsCSVReportParams,
  TipPerTransactionReportParams,
  EmployeeReportTableResult,
  EmployeeReportTableParams,
  UserTransactionsResult,
  EmployeeRevenueDownloadParams,
} from 'utils/apiClient/services/reports/types';
import { Tax } from 'CoreApi.Settings_V12';

const reportClientOpts: Options = {
  prefixUrl: `${config.CORE_URL}/api/reports`,
};

const genericClientOpts: Options = {
  prefixUrl: `${config.CORE_URL}/api`,
};

const dashboardPlusClientOpts: Options = {
  prefixUrl: `${config.DASHBOARD_PLUS_URL}/api/`,
};

const apiVersion8 = {
  'x-api-version': '8',
};

const apiVersion12 = {
  'x-api-version': '12',
};

const apiVersion13 = {
  'x-api-version': '13',
};

dayjs.extend(utc);
export const getSortParam = ({
  order,
  columnKey,
}: {
  order: string;
  columnKey: string;
}) => {
  if (!columnKey) {
    return 'product_name';
  }
  return [order === 'descend' ? '-' : '', columnKey].join('');
};

const customJsonHeader = {
  'Content-Type': 'application/json',
};

const dateFormat = 'DD.MM.YYYY';

const formatParams = (params?: ReportFilterOptions) => {
  const today = dayjs().format(dateFormat);
  const [start, end] = params?.dateRange
    ? params.dateRange.map((m) => (m ? m.format(dateFormat) : today))
    : [today, today];
  return {
    start,
    end,
    type: params?.type || 'daily',
    channel: params?.channel,
    sort: params?.sort,
  };
};

const getUrlParams = (url: string, params: ReportFilterOptions) => {
  const qsParams = qs.stringify(formatParams(params), { addQueryPrefix: true });
  const fullUrl = [url, qsParams].join('');
  return fullUrl;
};

export const useReportsApi = () => {
  const authenticatedApiClient = useAuthenticatedApiClient();
  const reportApiClient = authenticatedApiClient.extend(reportClientOpts);
  const genericApiClient = authenticatedApiClient.extend(genericClientOpts);
  const dashboardPlusClient = authenticatedApiClient.extend(
    dashboardPlusClientOpts
  );

  return {
    loadBasic: async (params: ReportFilterOptions) =>
      reportApiClient
        .get(getUrlParams('basic/', params), {
          headers: apiVersion12,
          timeout: 60000,
        })
        .json<Array<BasicReportRecord>>(),
    loadOrders: (params: ReportFilterOptions) =>
      genericApiClient
        .get(getUrlParams('orders/', params), {
          headers: apiVersion8,
        })
        .json<OrdersResponse>(),
    loadReturns: (params: ReportFilterOptions) =>
      genericApiClient
        .get(getUrlParams('orders/returns/', params), {
          headers: apiVersion12,
          timeout: 60000,
        })
        .json<ReturnsResponse>(),
    loadProductsSold: (params: ReportFilterOptions) =>
      reportApiClient
        .get(getUrlParams('products_sold/', params), {
          headers: apiVersion12,
          timeout: 60000,
        })
        .json<Array<ProductSoldReportRecord>>(),
    loadIncomeTransactions: (params: ReportFilterOptions) =>
      reportApiClient
        .get(getUrlParams('income_transactions/', params), {
          headers: apiVersion12,
          timeout: 60000,
        })
        .json<Array<IncomeTransactionsReportRecord>>(),
    emailBestSellersReport: (body: MailReportPayload) =>
      reportApiClient.post('products_sold/', {
        headers: { ...apiVersion12, ...customJsonHeader },
        body: JSON.stringify(body),
      }),
    emailTransactionsReport: (body: MailReportPayload) =>
      reportApiClient.post('income_transactions/', {
        headers: { ...apiVersion12, ...customJsonHeader },
        body: JSON.stringify(body),
      }),
    loadSalesReport: ({
      filters,
      sorter,
      pagination,
    }: SalesReportTableParams) => {
      const startDate = filters?.start;
      const endDate = filters?.end;

      const params = {
        start: startDate.format(),
        end: endDate.format(),
        type: filters?.type,
        categories:
          (filters?.categories?.length ?? 0) > 0 ? filters?.categories : null,
        page: typeof pagination !== 'boolean' ? pagination?.current : 1,
        limit: typeof pagination !== 'boolean' ? pagination?.pageSize : 10,
        //@ts-ignore
        order_by: sorter ? getSortParam(sorter) : null,
      };

      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      return reportApiClient
        .get(`sales/${paramsQs}`, {
          headers: apiVersion12,
          timeout: 60000,
        })
        .json<SalesReportsTableRecord>();
    },
    createGiftcardReport: (type: string, locale: string) => {
      const params = type === 'ACTIVE_ONLY' ? { filter: 'valid' } : null;
      const paramsQs = qs.stringify(
        { ...params, language: `${locale}.toLowerCase()` },
        {
          addQueryPrefix: true,
          skipNulls: true,
        }
      );
      return reportApiClient.post(['giftcards/', paramsQs].join(''), {
        headers: { ...customJsonHeader, ...apiVersion12 },
      });
    },
    loadInventoryReport: ({
      filters,
      sorter,
      pagination,
    }: InventoryReportTableParams) => {
      const startDate = filters?.start;
      const endDate = filters?.end;
      const uparams = {
        start: startDate ? startDate.format() : undefined,
        end: endDate ? endDate.format() : undefined,
        type: filters?.type || 'product',
        page: typeof pagination !== 'boolean' ? pagination?.current : 1,
        limit: typeof pagination !== 'boolean' ? pagination?.pageSize : 50,
        //@ts-ignore
        sort_by: sorter ? getSortParam(sorter) : null,
        channel_source: filters?.change_source ? filters.change_source : null,
        product_id: filters?.product_name ? filters.product_name : null,
        user_id: filters?.user ? filters.user : null,
      };

      const paramsQs = qs.stringify(uparams, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      const fullUrl = ['inventory_changes/', paramsQs].join('');
      return reportApiClient
        .get(fullUrl, {
          headers: apiVersion12,
        })
        .json<InventoryReportTableResult>();
    },
    async loadGiftcardsReport(
      tableParams: GiftcardReportTableParams,
      searchQuery: string | null
    ) {
      const { pagination } = tableParams;
      const params = {
        page:
          typeof pagination !== 'boolean' && !searchQuery
            ? pagination?.current
            : 1,
        limit: typeof pagination !== 'boolean' ? pagination?.pageSize : 50,
        ...(searchQuery !== null &&
          searchQuery !== '' && {
            redeem_code: searchQuery,
          }),
      };
      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });

      const output = await genericApiClient
        .get('giftcards/', {
          searchParams: paramsQs,
          headers: apiVersion12,
        })
        .json<GiftcardReportTableResult>();

      return output;
    },
    loadBatchReports: async (report_type: string) => {
      const params = {
        batch_type:
          report_type === 'product_history'
            ? ['product_history']
            : ['invoice_returns', 'zreports', 'xls_report', 'pre_accounting'],
      };

      const fullUrl = `batch_reports/?${qs.stringify(params, {
        skipNulls: true,
        arrayFormat: 'comma',
      })}`;
      const result = await reportApiClient
        .get(fullUrl, {
          headers: apiVersion12,
        })
        .json<Array<BatchReportRecord>>();
      return result;
    },
    generateBatchReport: async (reportId: number, locale?: string) => {
      const fullUrl = `batch_reports/${reportId}/?language=${locale?.toLowerCase()}`;
      const result = await reportApiClient
        .post(fullUrl, {
          headers: apiVersion12,
        })
        .json();
      return result;
    },
    loadTaxes: () => genericApiClient.get('taxes/').json<Tax[]>(),
    loadTipsSummaryReport: (input: TipsSummaryReportParams) => {
      const startDate = input.filters?.created_at_start;
      const endDate = input.filters?.created_at_end;
      const { filters } = input;
      const params = {
        created_at_start: startDate
          ? dayjs.utc(startDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        created_at_end: endDate
          ? dayjs.utc(endDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        group_by: filters?.type,
        user_id: (filters?.users?.length ?? 0) > 0 ? filters?.users : null,
      };

      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      return genericApiClient
        .get(`tips/summary/${paramsQs}`, {
          headers: apiVersion13,
        })
        .json<Array<TipsSummaryReport>>();
    },
    loadTipsListReport: ({
      filters,
      cursor,
    }: TipPerTransactionReportParams) => {
      const startDate = filters?.created_at_start;
      const endDate = filters?.created_at_end;
      const params = {
        created_at_start: startDate
          ? dayjs.utc(startDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        created_at_end: endDate
          ? dayjs.utc(endDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        user_id: (filters?.users?.length ?? 0) > 0 ? filters?.users : null,
        limit: 20,
        cursor,
      };
      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      return genericApiClient
        .get(`tips/`, {
          headers: apiVersion13,
          searchParams: paramsQs,
        })
        .json<TipPerTransactionReportTableResult>();
    },
    loadEmployeesRevenueReport: ({
      filters,
      cursor,
      sorter,
      limit = 10,
    }: EmployeeReportTableParams) => {
      const startDate = filters?.start_date;
      const endDate = filters?.end_date;
      const params = {
        start_date: startDate
          ? dayjs.utc(startDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        end_date: endDate
          ? dayjs.utc(endDate).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        limit,
        cursor,
        order_by:
          sorter?.columnKey === 'revenue_overall_total'
            ? `${sorter.order === 'descend' ? '-' : ''}revenue_overall_total`
            : 'revenue_overall_total',
      };
      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      return dashboardPlusClient
        .get(`users/full/`, {
          headers: apiVersion13,
          searchParams: paramsQs,
        })
        .json<EmployeeReportTableResult>();
    },
    loadUserTransactions: ({
      user_id,
      start_date,
      end_date,
      cursor,
      limit = 10,
    }: {
      user_id: number;
      start_date: Dayjs;
      end_date: Dayjs;
      cursor: string | null;
      limit?: number;
    }) => {
      const params = {
        user_id,
        start_date: start_date
          ? dayjs.utc(start_date).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        end_date: end_date
          ? dayjs.utc(end_date).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : undefined,
        limit,
        cursor,
      };
      const paramsQs = qs.stringify(params, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });

      return dashboardPlusClient
        .get(`user_transactions/`, {
          headers: apiVersion13,
          searchParams: paramsQs,
        })
        .json<UserTransactionsResult>();
    },

    createEmployeeRevenueReport: async (
      params: EmployeeRevenueDownloadParams
    ) => {
      const startDate = params?.start_date;
      const endDate = params?.end_date;
      const qParams = {
        start_date: dayjs.utc(startDate).toISOString(),
        end_date: dayjs.utc(endDate).toISOString(),
        report_name: params.report_name,
      };

      return dashboardPlusClient
        .post(`excel_reports_download/`, {
          headers: {
            ...apiVersion13,
            ...customJsonHeader,
            'Accept-Language': params.locale.toLowerCase(),
          },
          body: JSON.stringify(qParams),
        })
        .blob();
    },
    createTipsCSVReport: async (
      params: TipsCSVReportParams & { locale: string }
    ) => {
      const startDate = params?.created_at_start;
      const endDate = params?.created_at_end;
      const qParams = {
        created_at_start: dayjs
          .utc(startDate)
          .format('YYYY-MM-DDTHH:mm:ss.SSS'),
        created_at_end: dayjs.utc(endDate).format('YYYY-MM-DDTHH:mm:ss.SSS'),
        report_type: params.report_type,
        group_by: params.group_by,
        user_id: params.user_id,
        language: params.locale.toLowerCase(),
      };
      const paramsQs = qs.stringify(qParams, {
        addQueryPrefix: true,
        skipNulls: true,
        arrayFormat: 'comma',
      });
      return genericApiClient
        .get(`tips/csv_report/${paramsQs}`, {
          headers: {
            ...apiVersion13,
            ...customJsonHeader,
          },
        })
        .blob();
    },
  };
};
